import { Box, Divider, ListItem, styled } from '@mui/material'
import { Link, Typography, TypographyProps } from '@carto/meridian-ds/components'
import { theme } from '@carto/meridian-ds/theme'

const BlockquoteContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'spacingScale' })<{
  spacingScale: number
}>(({ theme, spacingScale }) => ({
  borderLeft: `2px solid ${theme.palette.grey[200]}`,
  margin: theme.spacing(1 * spacingScale, 0),
  paddingLeft: theme.spacing(2 * spacingScale),
  '& p': {
    margin: 0,
    color: theme.palette.text.secondary
  }
}))

const CodeInlineTypography = styled(Typography, { shouldForwardProp: (prop) => prop !== 'spacingScale' })<{
  spacingScale: number
}>(({ theme, spacingScale }) => ({
  border: `1px solid ${theme.palette.action.focus}`,
  borderRadius: theme.spacing(0.25),

  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.black[4],

  padding: theme.spacing(0.125 * spacingScale, 0.5 * spacingScale, 0),
  fontSize: `calc(${theme.typography.code3.fontSize} * ${spacingScale})`
}))

const CodeContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'spacingScale' })<{
  spacingScale: number
}>(({ theme, spacingScale }) => ({
  border: `1px solid ${theme.palette.action.focus}`,
  borderRadius: theme.spacing(0.25),

  color: theme.palette.text.primary,
  backgroundColor: theme.palette.black[4],

  padding: theme.spacing(1.5 * spacingScale),
  lineHeight: theme.spacing(2 * spacingScale),
  maxHeight: theme.spacing(40 * spacingScale),
  overflowY: 'auto',
  whiteSpace: 'break-spaces',

  ...theme.typography.code3,
  fontSize: `calc(${theme.typography.code3.fontSize} * ${spacingScale})`
}))

const MarkdownImg = styled('img')(() => ({
  maxWidth: '100%'
}))

const MarkdownListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'spacingScale' && prop !== 'variant'
})<{
  spacingScale: number
  variant: string
}>(({ theme, spacingScale, variant }) => ({
  display: 'list-item',
  padding: theme.spacing(0.25 * spacingScale),
  [`::marker`]: {
    ...theme.typography[variant]
  }
}))

export type MarkdownBaseTypographyVariant = 'caption' | 'body2'

const typographyVariantMapping: Record<
  MarkdownBaseTypographyVariant,
  Record<string, NonNullable<TypographyProps['variant']>>
> = {
  body2: {
    h1: 'h4',
    h2: 'h5',
    h3: 'h6',
    h4: 'subtitle1',
    main: 'body2',
    code: 'code3'
  },
  caption: {
    h1: 'h5',
    h2: 'h6',
    h3: 'subtitle1',
    h4: 'subtitle2',
    main: 'caption',
    code: 'code3'
  }
}
const getMarkDownComponents = ({ baseTypographyVariant }: { baseTypographyVariant: MarkdownBaseTypographyVariant }) => {
  const variantProps = typographyVariantMapping[baseTypographyVariant]
  const spacingScale = baseTypographyVariant === 'caption' ? 0.875 : 1

  return {
    h1: (node) => {
      return (
        <Typography color="inherit" variant={variantProps.h1} mb={1.5 * spacingScale}>
          {node.children}
        </Typography>
      )
    },
    h2: (node) => {
      return (
        <Typography color="inherit" variant={variantProps.h2} mb={1.25 * spacingScale}>
          {node.children}
        </Typography>
      )
    },
    h3: (node) => {
      return (
        <Typography color="inherit" variant={variantProps.h3} mb={1 * spacingScale}>
          {node.children}
        </Typography>
      )
    },
    h4: (node) => {
      return (
        <Typography color="inherit" variant={variantProps.h4} mb={0.75 * spacingScale}>
          {node.children}
        </Typography>
      )
    },
    p: (node) => {
      return (
        <Typography color="inherit" variant={variantProps.main} py={0.5 * spacingScale} component="div">
          {node.children}
        </Typography>
      )
    },
    a: (node) => {
      return (
        <Link href={node.href} target="_blank">
          {node.children}
        </Link>
      )
    },
    ol: (node) => {
      return (
        <ol
          style={{
            paddingLeft: theme.spacing(2.5 * spacingScale),
            marginTop: theme.spacing(1 * spacingScale),
            marginBottom: theme.spacing(1 * spacingScale)
          }}
        >
          {node.children}
        </ol>
      )
    },
    ul: (node) => {
      return (
        <ul
          style={{
            paddingLeft: theme.spacing(2.5 * spacingScale),
            marginTop: theme.spacing(1 * spacingScale),
            marginBottom: theme.spacing(1 * spacingScale)
          }}
        >
          {node.children}
        </ul>
      )
    },
    li: (node) => (
      <MarkdownListItem spacingScale={spacingScale} variant={variantProps.main}>
        <Typography color="inherit" component="div" variant={variantProps.main}>
          {node.children}
        </Typography>
      </MarkdownListItem>
    ),
    code: (node) => {
      if (node.inline) {
        return (
          <CodeInlineTypography variant={variantProps.code} weight="strong" spacingScale={spacingScale}>
            {node.children}
          </CodeInlineTypography>
        )
      } else {
        return <CodeContainer spacingScale={spacingScale}>{node.children}</CodeContainer>
      }
    },
    blockquote: (node) => {
      return (
        <BlockquoteContainer spacingScale={spacingScale}>
          <Typography color="inherit" variant={variantProps.main}>
            {node.children}
          </Typography>
        </BlockquoteContainer>
      )
    },
    img: (node) => {
      return (
        <Box mb={1 * spacingScale}>
          <MarkdownImg src={node.src} />
        </Box>
      )
    },
    hr: () => {
      return (
        <Box mt={2 * spacingScale} mb={2 * spacingScale}>
          <Divider />
        </Box>
      )
    }
  }
}

export default getMarkDownComponents
